import { hideLanguageSelectorView } from 'actions/ui/ui';
import { ErrorComponent } from 'components/Error/Error';
import { NOOP } from 'constants/actions';
import { Header } from 'containers/Header/Header';
import { PaymentSteps } from 'containers/PaymentSteps/PaymentSteps';
import { useCardElementFlowExperiment } from 'hooks/useCardElementFlowExperiment/useCardElementFlowExperiment';
import { useIsProduction } from 'hooks/useIsProduction/useIsProduction';
import { useModalContext } from 'hooks/useModalContext';
import { useScrollability } from 'hooks/useScrollability/useScrollability';
import { useTheme } from 'hooks/useTheme';
import { ReactComponent as Arrows } from 'icons/arrows-icon.svg';
import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLanguageSelectorViewVisible } from 'selectors/ui/ui';
import { Chat } from '../Chat/Chat';
import { CloseModalButton } from '../CloseModalButton/CloseModalButton';
import { CloseModalConfirmation } from '../CloseModalConfirmation/CloseModalConfirmation';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Footer } from '../Footer/Footer';
import { Notifications } from '../Notifications/Notifications';

import './Checkout.scss';

const Modal = lazy(() =>
  import('components/ui/Modal/Modal').then(module => ({
    default: module.Modal,
  })),
);

const LanguageSelectorView = lazy(() =>
  import('components/LanguageSelectorView/LanguageSelectorView').then(
    module => ({ default: module.LanguageSelectorView }),
  ),
);

export const Checkout = ({ onRestart = NOOP } = {}) => {
  useCardElementFlowExperiment();
  const isProduction = useIsProduction();
  const { closeButton, header, footer } = useTheme();
  const {
    cancelCloseModal,
    cancelPayment,
    closeModal,
    confirmCloseModal,
    hasError,
    isClosingModal,
    isModalOpen,
  } = useModalContext();
  const [contentRef, { scrollable, endOfScroll }, contentElement] =
    useScrollability();
  const dispatch = useDispatch();
  const isLanguageSelectorViewVisible = useSelector(
    getIsLanguageSelectorViewVisible,
  );

  const handleCloseLanguageSelectorView = () => {
    dispatch(hideLanguageSelectorView());
  };

  const handleScrollToBottom = () => {
    if (contentElement) {
      contentElement.scrollTop = contentElement.scrollHeight;
    }
  };

  return (
    <Suspense fallback={<div />}>
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <Notifications />
        {closeButton && <CloseModalButton onClick={closeModal} />}
        {header && <Header />}
        {isClosingModal && (
          <CloseModalConfirmation
            onCancel={cancelCloseModal}
            onConfirm={confirmCloseModal}
          />
        )}
        <ErrorBoundary className={isClosingModal ? 'hidden' : ''}>
          <div
            className={`Checkout-content ${isClosingModal ? 'hidden' : ''}`}
            ref={contentRef}
          >
            {hasError ? (
              <ErrorComponent.Default />
            ) : (
              <div
                className={`h-full ${isLanguageSelectorViewVisible ? 'hidden' : ''}`}
              >
                <PaymentSteps
                  onClose={closeModal}
                  onCancelPayment={cancelPayment}
                  onRestart={onRestart}
                />
              </div>
            )}
            {!hasError && isLanguageSelectorViewVisible && (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center p-4">
                    Loading...
                  </div>
                }
              >
                <LanguageSelectorView
                  onClose={handleCloseLanguageSelectorView}
                />
              </Suspense>
            )}
          </div>
        </ErrorBoundary>
        {isProduction && <Chat />}
        <div className="relative">
          {footer && <Footer />}
          {scrollable && !endOfScroll ? (
            <button
              className="absolute size-9 rounded-full left-1/2 -ml-[18px] -top-[18px] bg-white shadow-sm animate-bounce z-50 flex items-center justify-center"
              aria-label="Scroll to bottom"
              onClick={handleScrollToBottom}
            >
              <Arrows aria-hidden />
            </button>
          ) : null}
        </div>
      </Modal>
    </Suspense>
  );
};

Checkout.propTypes = {
  onRestart: PropTypes.func.isRequired,
};
